import React from 'react';
import { Document, Page, Text, View, StyleSheet } from '@react-pdf/renderer';

// Professional color scheme
const colors = {
  primary: '#003366',
  secondary: '#0056A8',
  accent: '#60A5FA',
  success: '#10B981',
  warning: '#F59E0B',
  danger: '#EF4444',
  dark: '#1E293B',
  light: '#F8FAFC',
  muted: '#64748B',
  border: '#E2E8F0',
};

// Styles for PDF
const styles = StyleSheet.create({
  page: {
    padding: 40,
    backgroundColor: '#FFFFFF',
    fontFamily: 'Helvetica',
  },
  coverPage: {
    padding: 0,
    backgroundColor: colors.primary,
  },
  coverContent: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 60,
  },
  coverTitle: {
    fontSize: 36,
    fontWeight: 'bold',
    color: '#FFFFFF',
    marginBottom: 10,
    textAlign: 'center',
  },
  coverSubtitle: {
    fontSize: 18,
    color: colors.accent,
    marginBottom: 20,
    textAlign: 'center',
  },
  coverMetric: {
    flexDirection: 'row',
    justifyContent: 'center',
    gap: 30,
    marginTop: 30,
  },
  coverMetricBox: {
    padding: 15,
    backgroundColor: 'rgba(255,255,255,0.1)',
    borderRadius: 8,
    alignItems: 'center',
    width: 120,
  },
  coverMetricValue: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#FFFFFF',
  },
  coverMetricLabel: {
    fontSize: 10,
    color: colors.accent,
    marginTop: 5,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 15,
    paddingBottom: 10,
    borderBottomWidth: 2,
    borderBottomColor: colors.primary,
  },
  headerTitle: {
    fontSize: 10,
    color: colors.muted,
  },
  sectionTitle: {
    fontSize: 22,
    fontWeight: 'bold',
    color: colors.primary,
    marginBottom: 15,
  },
  sectionSubtitle: {
    fontSize: 12,
    color: colors.muted,
    marginBottom: 12,
  },
  paragraph: {
    fontSize: 10,
    lineHeight: 1.5,
    color: colors.dark,
    marginBottom: 10,
  },
  table: {
    marginVertical: 10,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 4,
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: colors.primary,
    padding: 8,
  },
  tableHeaderCell: {
    flex: 1,
    fontSize: 9,
    fontWeight: 'bold',
    color: '#FFFFFF',
  },
  tableHeaderCellRight: {
    flex: 1,
    fontSize: 9,
    fontWeight: 'bold',
    color: '#FFFFFF',
    textAlign: 'right',
  },
  tableRow: {
    flexDirection: 'row',
    padding: 8,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  tableRowAlt: {
    backgroundColor: colors.light,
  },
  tableCell: {
    flex: 1,
    fontSize: 9,
    color: colors.dark,
  },
  tableCellRight: {
    flex: 1,
    fontSize: 9,
    color: colors.dark,
    textAlign: 'right',
  },
  tableCellBold: {
    flex: 1,
    fontSize: 9,
    color: colors.dark,
    fontWeight: 'bold',
  },
  tableCellSuccess: {
    flex: 1,
    fontSize: 9,
    color: colors.success,
    fontWeight: 'bold',
    textAlign: 'right',
  },
  metricsRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 15,
  },
  metricCard: {
    width: '23%',
    padding: 12,
    backgroundColor: colors.light,
    borderRadius: 6,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: colors.border,
  },
  metricValue: {
    fontSize: 16,
    fontWeight: 'bold',
    color: colors.primary,
    marginBottom: 3,
  },
  metricLabel: {
    fontSize: 8,
    color: colors.muted,
    textAlign: 'center',
  },
  summaryBox: {
    backgroundColor: '#ECFDF5',
    borderLeftWidth: 4,
    borderLeftColor: colors.success,
    padding: 12,
    marginVertical: 10,
  },
  summaryText: {
    fontSize: 10,
    color: colors.dark,
  },
  footer: {
    position: 'absolute',
    bottom: 20,
    left: 40,
    right: 40,
    flexDirection: 'row',
    justifyContent: 'space-between',
    borderTopWidth: 1,
    borderTopColor: colors.border,
    paddingTop: 10,
  },
  footerText: {
    fontSize: 8,
    color: colors.muted,
  },
  yearHeader: {
    backgroundColor: colors.primary,
    padding: 10,
    marginBottom: 10,
    borderRadius: 4,
  },
  yearHeaderText: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#FFFFFF',
  },
  yearPhase: {
    fontSize: 10,
    color: colors.accent,
  },
  subSectionTitle: {
    fontSize: 11,
    fontWeight: 'bold',
    color: colors.secondary,
    marginTop: 10,
    marginBottom: 5,
  },
  noteBox: {
    backgroundColor: '#FEF3C7',
    borderLeftWidth: 3,
    borderLeftColor: colors.warning,
    padding: 10,
    marginTop: 10,
  },
  noteText: {
    fontSize: 9,
    color: colors.dark,
  },
});

// Format currency
const formatCurrency = (num) => {
  if (num >= 1000000) {
    return `$${(num / 1000000).toFixed(2)}M`;
  } else if (num >= 1000) {
    return `$${(num / 1000).toFixed(0)}K`;
  }
  return `$${num.toLocaleString()}`;
};

// Page header component
const PageHeader = ({ title }) => (
  <View style={styles.header}>
    <Text style={styles.headerTitle}>AskIsaac Inc. | Financial Projections</Text>
    <Text style={styles.headerTitle}>{title}</Text>
  </View>
);

// Page footer component
const PageFooter = () => (
  <View style={styles.footer}>
    <Text style={styles.footerText}>© 2026 AskIsaac Inc. Confidential</Text>
    <Text style={styles.footerText}>3-Year Financial Projections (Lean Model)</Text>
  </View>
);

// Main PDF Document
// Real-estate overlay — Plan C only (Plans A + B retired). Mirrors FinancialProjectionsPage.jsx.
const REAL_ESTATE_OVERLAY = {
  ronan: {
    label: "Plan C \u2014 16651 Ronan Rd HQ",
    short: "16651 Ronan Rd, Houston, TX 77060",
    sqft: "22,750 SF \u00b7 3 Stories \u00b7 Class-A Corporate Office \u00b7 5.08 Acres",
    totalCapital: 4_490_000,
    sbaLoan: 3_456_000,
    equity: 384_000,
    monthlyDebt: 32_631,
    annualDebt: 391_571,
    revenue: [4_541_860, 7_585_776, 11_641_336],
    netIncome: [948_189, 2_366_184, 4_254_596],
    rental: [0, 0, 0],
    dscr: [3.38, 6.71, 11.21],
    accent: "#B45309",
  },
};

const fmtCurrency = (n) => {
  if (Math.abs(n) >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (Math.abs(n) >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
  return `$${n.toLocaleString()}`;
};

const FinancialProjectionsPDF = ({ scenario = "ronan" }) => {
  const ov = REAL_ESTATE_OVERLAY[scenario] || REAL_ESTATE_OVERLAY.ronan;
  const totalRev = ov.revenue;
  const netInc = ov.netIncome;
  const totalExp = totalRev.map((r, i) => r - netInc[i]);
  const totalProfit = netInc.reduce((s, n) => s + n, 0);

  return (
  <Document>
    {/* Cover Page */}
    <Page size="A4" style={styles.coverPage}>
      <View style={styles.coverContent}>
        <Text style={styles.coverTitle}>Financial Projections</Text>
        <Text style={styles.coverSubtitle}>3-Year Forecast | {ov.label}</Text>
        <Text style={{ fontSize: 12, color: '#94A3B8', textAlign: 'center', marginTop: 4 }}>
          {ov.short}
        </Text>
        <Text style={{ fontSize: 10, color: '#94A3B8', textAlign: 'center', marginTop: 2 }}>
          {ov.sqft}
        </Text>
        <Text style={{ fontSize: 14, color: '#94A3B8', textAlign: 'center', marginTop: 14 }}>
          Sterile Mate LLC
        </Text>

        <View style={styles.coverMetric}>
          <View style={styles.coverMetricBox}>
            <Text style={styles.coverMetricValue}>{fmtCurrency(totalRev[2])}</Text>
            <Text style={styles.coverMetricLabel}>Year 3 Revenue</Text>
          </View>
          <View style={styles.coverMetricBox}>
            <Text style={{ ...styles.coverMetricValue, color: colors.success }}>{fmtCurrency(netInc[2])}</Text>
            <Text style={styles.coverMetricLabel}>Year 3 Net Income</Text>
          </View>
          <View style={styles.coverMetricBox}>
            <Text style={styles.coverMetricValue}>{fmtCurrency(totalProfit)}</Text>
            <Text style={styles.coverMetricLabel}>3-Year Profit</Text>
          </View>
        </View>

        <View style={{ marginTop: 30, padding: 12, backgroundColor: 'rgba(16, 185, 129, 0.2)', borderRadius: 8, width: '80%' }}>
          <Text style={{ fontSize: 12, color: colors.success, textAlign: 'center', fontWeight: 'bold' }}>
            SBA 7(a): {fmtCurrency(ov.sbaLoan)} loan {'\u00b7'} {fmtCurrency(ov.monthlyDebt)}/mo {'\u00b7'} DSCR {ov.dscr[0].toFixed(2)}x \u2192 {ov.dscr[2].toFixed(2)}x
          </Text>
          {scenario === "northsam" && (
            <Text style={{ fontSize: 9, color: '#A7F3D0', textAlign: 'center', marginTop: 4 }}>
              {fmtCurrency(ov.rental[0] + ov.rental[1] + ov.rental[2])} multi-tenant rental income offsets the larger debt service
            </Text>
          )}
          {scenario === "ronan" && (
            <Text style={{ fontSize: 9, color: '#FCD34D', textAlign: 'center', marginTop: 4 }}>
              {fmtCurrency(ov.rental[0] + ov.rental[1] + ov.rental[2])} guaranteed Floor 2 salon-suite anchor lease income across 3 years
            </Text>
          )}
        </View>

        <Text style={{ fontSize: 10, color: '#64748B', textAlign: 'center', marginTop: 30 }}>
          Confidential | For Qualified Investors Only
        </Text>
      </View>
    </Page>

    {/* Capital Plan & Real Estate Page (scenario-specific) */}
    <Page size="A4" style={styles.page}>
      <PageHeader title={`SBA Capital Plan \u2014 ${ov.label}`} />
      <Text style={styles.sectionTitle}>SBA Capital Plan</Text>
      <Text style={{ fontSize: 11, color: colors.muted, marginBottom: 14 }}>
        {ov.short} {'\u00b7'} {ov.sqft}
      </Text>

      <View style={styles.metricsRow}>
        <View style={{ ...styles.metricCard, backgroundColor: '#EFF6FF' }}>
          <Text style={styles.metricValue}>{fmtCurrency(ov.totalCapital)}</Text>
          <Text style={styles.metricLabel}>Total Capital</Text>
        </View>
        <View style={{ ...styles.metricCard, backgroundColor: '#ECFDF5' }}>
          <Text style={{ ...styles.metricValue, color: colors.success }}>{fmtCurrency(ov.sbaLoan)}</Text>
          <Text style={styles.metricLabel}>SBA 7(a) Loan</Text>
        </View>
        <View style={{ ...styles.metricCard, backgroundColor: '#FEF3C7' }}>
          <Text style={{ ...styles.metricValue, color: colors.warning }}>{fmtCurrency(ov.equity)}</Text>
          <Text style={styles.metricLabel}>Equity Injection</Text>
        </View>
        <View style={{ ...styles.metricCard, backgroundColor: '#F0FDF4' }}>
          <Text style={styles.metricValue}>{fmtCurrency(ov.monthlyDebt)}/mo</Text>
          <Text style={styles.metricLabel}>Debt Service</Text>
        </View>
      </View>

      <Text style={styles.subSectionTitle}>3-Year P&amp;L (with Capital Plan overlay)</Text>
      <View style={styles.table}>
        <View style={styles.tableHeader}>
          <Text style={styles.tableHeaderCell}>Line Item</Text>
          <Text style={styles.tableHeaderCellRight}>Year 1</Text>
          <Text style={styles.tableHeaderCellRight}>Year 2</Text>
          <Text style={styles.tableHeaderCellRight}>Year 3</Text>
        </View>
        {[
          ["Total Revenue (incl. rental)", totalRev],
          ["Multi-tenant Rental Income", ov.rental],
          ["Total Expenses (incl. debt + property)", totalExp],
          ["SBA Annual Debt Service (P&I)", [ov.annualDebt, ov.annualDebt, ov.annualDebt]],
          ["Profit After Tax", netInc],
        ].map(([label, vals], i) => (
          <View key={i} style={styles.tableRow}>
            <Text style={styles.tableCell}>{label}</Text>
            {vals.map((v, j) => (
              <Text key={j} style={styles.tableCellRight}>{fmtCurrency(v)}</Text>
            ))}
          </View>
        ))}
        <View style={{ ...styles.tableRow, backgroundColor: '#F0FDF4' }}>
          <Text style={{ ...styles.tableCell, fontWeight: 'bold' }}>DSCR</Text>
          {ov.dscr.map((d, j) => (
            <Text key={j} style={{ ...styles.tableCellRight, fontWeight: 'bold', color: colors.success }}>
              {d.toFixed(2)}x
            </Text>
          ))}
        </View>
      </View>

      {scenario === "northsam" && (
        <View style={{ marginTop: 14, padding: 10, backgroundColor: '#ECFDF5', borderRadius: 6 }}>
          <Text style={{ fontSize: 11, fontWeight: 'bold', color: colors.success, marginBottom: 4 }}>
            2SIXTY3 Multi-Tenant Rental Strategy
          </Text>
          <Text style={{ fontSize: 9, color: colors.dark, lineHeight: 1.4 }}>
            87,282 SF total {'\u00b7'} Sterile Mate occupies ~30,000 SF {'\u00b7'} ~50,000 SF surplus leasable.
            Class-B Beltway 8 rates ~$14-16/SF NNN. Lease-up: Y1 50% / Y2 75% / Y3 90%.
            Rental income offsets the higher debt service vs Plan A while the building appreciates as a separate asset.
          </Text>
        </View>
      )}
      {scenario === "ronan" && (
        <View style={{ marginTop: 14, padding: 10, backgroundColor: '#FEF3C7', borderRadius: 6 }}>
          <Text style={{ fontSize: 11, fontWeight: 'bold', color: '#B45309', marginBottom: 4 }}>
            16651 Ronan Rd \u2014 Floor 2 Salon-Suite Anchor Tenant
          </Text>
          <Text style={{ fontSize: 9, color: colors.dark, lineHeight: 1.4 }}>
            22,750 SF Class-A 3-story HQ on 5.08 acres with 88 available on-site parking spaces. Sterile Mate occupies Floor 1 (10,438 SF) + Floor 3 (executive suite).
            Floor 2 (12,640 SF) is leased to an established Greenspoint salon-suite operator who currently runs 2 leased
            floors in a nearby high-rise. Triple-net (NNN) lease at $15\u2013$18/SF generates {fmtCurrency(ov.rental[0])} (Y1, 2 mo free) \u2192
            {' '}{fmtCurrency(ov.rental[2])} (Y3) of guaranteed anchor rental income. No lease-up risk. The {fmtCurrency(500_000)}{' '}
            of operating capital is structured as a separate revolving Line of Credit, preserving the SBA 7(a) facility
            for real estate + renovation only.
          </Text>
        </View>
      )}
    </Page>

    {/* Executive Summary */}
    <Page size="A4" style={styles.page}>
      <PageHeader title="Executive Summary" />
      <Text style={styles.sectionTitle}>Executive Summary</Text>
      
      <View style={styles.metricsRow}>
        <View style={{ ...styles.metricCard, backgroundColor: '#EFF6FF' }}>
          <Text style={styles.metricValue}>$745K</Text>
          <Text style={styles.metricLabel}>Year 1 Revenue</Text>
        </View>
        <View style={{ ...styles.metricCard, backgroundColor: '#ECFDF5' }}>
          <Text style={styles.metricValue}>$2.86M</Text>
          <Text style={styles.metricLabel}>Year 2 Revenue</Text>
        </View>
        <View style={{ ...styles.metricCard, backgroundColor: '#F0FDF4' }}>
          <Text style={styles.metricValue}>$8.01M</Text>
          <Text style={styles.metricLabel}>Year 3 Revenue</Text>
        </View>
        <View style={{ ...styles.metricCard, backgroundColor: '#FEF3C7' }}>
          <Text style={{ ...styles.metricValue, color: colors.success }}>$5.56M</Text>
          <Text style={styles.metricLabel}>3-Year Net Profit</Text>
        </View>
      </View>

      <Text style={styles.subSectionTitle}>Year-over-Year Summary</Text>
      <View style={styles.table}>
        <View style={styles.tableHeader}>
          <Text style={styles.tableHeaderCell}>Metric</Text>
          <Text style={styles.tableHeaderCellRight}>Year 1</Text>
          <Text style={styles.tableHeaderCellRight}>Year 2</Text>
          <Text style={styles.tableHeaderCellRight}>Year 3</Text>
        </View>
        <View style={styles.tableRow}>
          <Text style={styles.tableCell}>Phase</Text>
          <Text style={styles.tableCellRight}>Launch</Text>
          <Text style={styles.tableCellRight}>Growth</Text>
          <Text style={styles.tableCellRight}>Scale</Text>
        </View>
        <View style={{ ...styles.tableRow, ...styles.tableRowAlt }}>
          <Text style={styles.tableCell}>Health System Customers</Text>
          <Text style={styles.tableCellRight}>4</Text>
          <Text style={styles.tableCellRight}>14</Text>
          <Text style={styles.tableCellRight}>32</Text>
        </View>
        <View style={styles.tableRow}>
          <Text style={styles.tableCell}>Total Locations</Text>
          <Text style={styles.tableCellRight}>45</Text>
          <Text style={styles.tableCellRight}>180</Text>
          <Text style={styles.tableCellRight}>520</Text>
        </View>
        <View style={{ ...styles.tableRow, ...styles.tableRowAlt }}>
          <Text style={styles.tableCell}>Total Revenue</Text>
          <Text style={styles.tableCellRight}>$745,000</Text>
          <Text style={styles.tableCellRight}>$2,861,000</Text>
          <Text style={styles.tableCellRight}>$8,010,000</Text>
        </View>
        <View style={styles.tableRow}>
          <Text style={styles.tableCell}>Total Expenses</Text>
          <Text style={styles.tableCellRight}>$683,050</Text>
          <Text style={styles.tableCellRight}>$1,849,000</Text>
          <Text style={styles.tableCellRight}>$4,127,000</Text>
        </View>
        <View style={{ ...styles.tableRow, ...styles.tableRowAlt }}>
          <Text style={styles.tableCellBold}>Net Income</Text>
          <Text style={{ ...styles.tableCellRight, color: colors.warning, fontWeight: 'bold' }}>$61,950</Text>
          <Text style={styles.tableCellSuccess}>$1,012,000</Text>
          <Text style={styles.tableCellSuccess}>$3,883,000</Text>
        </View>
        <View style={styles.tableRow}>
          <Text style={styles.tableCell}>Profit Margin</Text>
          <Text style={styles.tableCellRight}>8.3%</Text>
          <Text style={styles.tableCellRight}>35.4%</Text>
          <Text style={styles.tableCellRight}>48.5%</Text>
        </View>
        <View style={{ ...styles.tableRow, ...styles.tableRowAlt }}>
          <Text style={styles.tableCell}>Headcount</Text>
          <Text style={styles.tableCellRight}>4</Text>
          <Text style={styles.tableCellRight}>9</Text>
          <Text style={styles.tableCellRight}>19</Text>
        </View>
      </View>

      <View style={styles.summaryBox}>
        <Text style={styles.summaryText}>
          <Text style={{ fontWeight: 'bold' }}>Key Insight:</Text> With CEO at fair market salary ($175K), Year 1 is break-even with 8% margin. The $900K investment provides 12-month runway cushion and growth acceleration capital. Years 2-3 scale rapidly with 35-48% margins.
        </Text>
      </View>

      <Text style={styles.subSectionTitle}>Unit Economics</Text>
      <View style={styles.metricsRow}>
        <View style={styles.metricCard}>
          <Text style={{ ...styles.metricValue, fontSize: 14 }}>$86K-$108K</Text>
          <Text style={styles.metricLabel}>Customer LTV</Text>
        </View>
        <View style={styles.metricCard}>
          <Text style={{ ...styles.metricValue, fontSize: 14 }}>$22K-$33K</Text>
          <Text style={styles.metricLabel}>Customer CAC</Text>
        </View>
        <View style={styles.metricCard}>
          <Text style={{ ...styles.metricValue, fontSize: 14, color: colors.success }}>3.3-3.9x</Text>
          <Text style={styles.metricLabel}>LTV:CAC Ratio</Text>
        </View>
        <View style={styles.metricCard}>
          <Text style={{ ...styles.metricValue, fontSize: 14 }}>87-88%</Text>
          <Text style={styles.metricLabel}>Gross Margin</Text>
        </View>
      </View>
      <PageFooter />
    </Page>

    {/* Year 1 Detail */}
    <Page size="A4" style={styles.page}>
      <PageHeader title="Year 1 - Launch Phase" />
      <View style={styles.yearHeader}>
        <Text style={styles.yearHeaderText}>Year 1 — Launch Phase</Text>
        <Text style={styles.yearPhase}>Team of 4 | Break-even with runway</Text>
      </View>

      <View style={styles.metricsRow}>
        <View style={{ ...styles.metricCard, backgroundColor: '#EFF6FF' }}>
          <Text style={styles.metricValue}>$745K</Text>
          <Text style={styles.metricLabel}>Total Revenue</Text>
        </View>
        <View style={{ ...styles.metricCard, backgroundColor: '#FEE2E2' }}>
          <Text style={styles.metricValue}>$683K</Text>
          <Text style={styles.metricLabel}>Total Expenses</Text>
        </View>
        <View style={{ ...styles.metricCard, backgroundColor: '#FEF3C7' }}>
          <Text style={{ ...styles.metricValue, color: colors.warning }}>$62K</Text>
          <Text style={styles.metricLabel}>Net Income</Text>
        </View>
        <View style={{ ...styles.metricCard, backgroundColor: '#FEF3C7' }}>
          <Text style={{ ...styles.metricValue, color: colors.warning }}>8.3%</Text>
          <Text style={styles.metricLabel}>Profit Margin</Text>
        </View>
      </View>

      <Text style={styles.subSectionTitle}>Customer Metrics</Text>
      <View style={styles.table}>
        <View style={styles.tableHeader}>
          <Text style={styles.tableHeaderCell}>Metric</Text>
          <Text style={styles.tableHeaderCellRight}>Value</Text>
        </View>
        <View style={styles.tableRow}>
          <Text style={styles.tableCell}>Health System Customers</Text>
          <Text style={styles.tableCellRight}>4</Text>
        </View>
        <View style={{ ...styles.tableRow, ...styles.tableRowAlt }}>
          <Text style={styles.tableCell}>Total Locations (Hospitals + Clinics)</Text>
          <Text style={styles.tableCellRight}>45 (5 + 40)</Text>
        </View>
        <View style={styles.tableRow}>
          <Text style={styles.tableCell}>Avg Revenue per Location/Month</Text>
          <Text style={styles.tableCellRight}>$1,200</Text>
        </View>
        <View style={{ ...styles.tableRow, ...styles.tableRowAlt }}>
          <Text style={styles.tableCell}>Avg Setup Fee per Customer</Text>
          <Text style={styles.tableCellRight}>$18,000</Text>
        </View>
      </View>

      <Text style={styles.subSectionTitle}>Revenue Breakdown</Text>
      <View style={styles.table}>
        <View style={styles.tableHeader}>
          <Text style={styles.tableHeaderCell}>Revenue Stream</Text>
          <Text style={styles.tableHeaderCellRight}>Amount</Text>
          <Text style={styles.tableHeaderCellRight}>% of Total</Text>
        </View>
        <View style={styles.tableRow}>
          <Text style={styles.tableCell}>Recurring (Platform Fees)</Text>
          <Text style={styles.tableCellRight}>$648,000</Text>
          <Text style={styles.tableCellRight}>87%</Text>
        </View>
        <View style={{ ...styles.tableRow, ...styles.tableRowAlt }}>
          <Text style={styles.tableCell}>Setup Fees</Text>
          <Text style={styles.tableCellRight}>$72,000</Text>
          <Text style={styles.tableCellRight}>10%</Text>
        </View>
        <View style={styles.tableRow}>
          <Text style={styles.tableCell}>Professional Services</Text>
          <Text style={styles.tableCellRight}>$25,000</Text>
          <Text style={styles.tableCellRight}>3%</Text>
        </View>
        <View style={{ ...styles.tableRow, ...styles.tableRowAlt, backgroundColor: '#EFF6FF' }}>
          <Text style={styles.tableCellBold}>TOTAL REVENUE</Text>
          <Text style={{ ...styles.tableCellRight, fontWeight: 'bold' }}>$745,000</Text>
          <Text style={styles.tableCellRight}>100%</Text>
        </View>
      </View>

      <Text style={styles.subSectionTitle}>Expense Breakdown</Text>
      <View style={styles.table}>
        <View style={styles.tableHeader}>
          <Text style={styles.tableHeaderCell}>Category</Text>
          <Text style={styles.tableHeaderCellRight}>Amount</Text>
          <Text style={styles.tableHeaderCellRight}>% of Total</Text>
        </View>
        <View style={styles.tableRow}>
          <Text style={styles.tableCell}>Personnel (8 FTE + 1 contractor)</Text>
          <Text style={styles.tableCellRight}>$570,025</Text>
          <Text style={styles.tableCellRight}>75%</Text>
        </View>
        <View style={{ ...styles.tableRow, ...styles.tableRowAlt }}>
          <Text style={styles.tableCell}>Infrastructure (Cloud, DB, Security)</Text>
          <Text style={styles.tableCellRight}>$52,800</Text>
          <Text style={styles.tableCellRight}>8%</Text>
        </View>
        <View style={styles.tableRow}>
          <Text style={styles.tableCell}>Marketing</Text>
          <Text style={styles.tableCellRight}>$40,000</Text>
          <Text style={styles.tableCellRight}>6%</Text>
        </View>
        <View style={{ ...styles.tableRow, ...styles.tableRowAlt }}>
          <Text style={styles.tableCell}>Operations (Legal, Insurance, etc.)</Text>
          <Text style={styles.tableCellRight}>$67,000</Text>
          <Text style={styles.tableCellRight}>10%</Text>
        </View>
        <View style={styles.tableRow}>
          <Text style={styles.tableCell}>Software & Tools</Text>
          <Text style={styles.tableCellRight}>$12,000</Text>
          <Text style={styles.tableCellRight}>1%</Text>
        </View>
        <View style={{ ...styles.tableRow, ...styles.tableRowAlt, backgroundColor: '#FEE2E2' }}>
          <Text style={styles.tableCellBold}>TOTAL EXPENSES</Text>
          <Text style={{ ...styles.tableCellRight, fontWeight: 'bold' }}>$683,050</Text>
          <Text style={styles.tableCellRight}>100%</Text>
        </View>
      </View>

      <View style={styles.noteBox}>
        <Text style={styles.noteText}>
          <Text style={{ fontWeight: 'bold' }}>Note:</Text> CEO capped at $125K. Team of 8 FTE + 1 outsourced developer ($5K/mo). Includes 3 sales reps ($42.5K + 15% commission), Office Manager/EA ($55K), Bookkeeper ($50K), Warehouse Coordinator ($46K), Sales Assistant ($40K). Benefits at 15% of FTE salaries.
        </Text>
      </View>
      <PageFooter />
    </Page>

    {/* Year 2 Detail */}
    <Page size="A4" style={styles.page}>
      <PageHeader title="Year 2 - Growth Phase" />
      <View style={styles.yearHeader}>
        <Text style={styles.yearHeaderText}>Year 2 — Growth Phase</Text>
        <Text style={styles.yearPhase}>Team of 9 | 284% revenue growth</Text>
      </View>

      <View style={styles.metricsRow}>
        <View style={{ ...styles.metricCard, backgroundColor: '#EFF6FF' }}>
          <Text style={styles.metricValue}>$2.86M</Text>
          <Text style={styles.metricLabel}>Total Revenue</Text>
        </View>
        <View style={{ ...styles.metricCard, backgroundColor: '#FEE2E2' }}>
          <Text style={styles.metricValue}>$1.85M</Text>
          <Text style={styles.metricLabel}>Total Expenses</Text>
        </View>
        <View style={{ ...styles.metricCard, backgroundColor: '#ECFDF5' }}>
          <Text style={{ ...styles.metricValue, color: colors.success }}>$1.01M</Text>
          <Text style={styles.metricLabel}>Net Income</Text>
        </View>
        <View style={{ ...styles.metricCard, backgroundColor: '#F0FDF4' }}>
          <Text style={{ ...styles.metricValue, color: colors.success }}>35.4%</Text>
          <Text style={styles.metricLabel}>Profit Margin</Text>
        </View>
      </View>

      <Text style={styles.subSectionTitle}>Customer Metrics</Text>
      <View style={styles.table}>
        <View style={styles.tableHeader}>
          <Text style={styles.tableHeaderCell}>Metric</Text>
          <Text style={styles.tableHeaderCellRight}>Value</Text>
          <Text style={styles.tableHeaderCellRight}>vs Y1</Text>
        </View>
        <View style={styles.tableRow}>
          <Text style={styles.tableCell}>Health System Customers</Text>
          <Text style={styles.tableCellRight}>14</Text>
          <Text style={{ ...styles.tableCellRight, color: colors.success }}>+250%</Text>
        </View>
        <View style={{ ...styles.tableRow, ...styles.tableRowAlt }}>
          <Text style={styles.tableCell}>Total Locations</Text>
          <Text style={styles.tableCellRight}>180 (22 + 158)</Text>
          <Text style={{ ...styles.tableCellRight, color: colors.success }}>+300%</Text>
        </View>
        <View style={styles.tableRow}>
          <Text style={styles.tableCell}>Avg Revenue per Location/Month</Text>
          <Text style={styles.tableCellRight}>$1,100</Text>
          <Text style={styles.tableCellRight}>-8% (volume)</Text>
        </View>
        <View style={{ ...styles.tableRow, ...styles.tableRowAlt }}>
          <Text style={styles.tableCell}>Avg Setup Fee per Customer</Text>
          <Text style={styles.tableCellRight}>$30,000</Text>
          <Text style={{ ...styles.tableCellRight, color: colors.success }}>+67%</Text>
        </View>
      </View>

      <Text style={styles.subSectionTitle}>Revenue Breakdown</Text>
      <View style={styles.table}>
        <View style={styles.tableHeader}>
          <Text style={styles.tableHeaderCell}>Revenue Stream</Text>
          <Text style={styles.tableHeaderCellRight}>Amount</Text>
          <Text style={styles.tableHeaderCellRight}>% of Total</Text>
        </View>
        <View style={styles.tableRow}>
          <Text style={styles.tableCell}>Recurring (Platform Fees)</Text>
          <Text style={styles.tableCellRight}>$2,376,000</Text>
          <Text style={styles.tableCellRight}>83%</Text>
        </View>
        <View style={{ ...styles.tableRow, ...styles.tableRowAlt }}>
          <Text style={styles.tableCell}>Setup Fees (10 new customers)</Text>
          <Text style={styles.tableCellRight}>$300,000</Text>
          <Text style={styles.tableCellRight}>10%</Text>
        </View>
        <View style={styles.tableRow}>
          <Text style={styles.tableCell}>Professional Services</Text>
          <Text style={styles.tableCellRight}>$120,000</Text>
          <Text style={styles.tableCellRight}>4%</Text>
        </View>
        <View style={{ ...styles.tableRow, ...styles.tableRowAlt }}>
          <Text style={styles.tableCell}>Expansion Revenue</Text>
          <Text style={styles.tableCellRight}>$65,000</Text>
          <Text style={styles.tableCellRight}>2%</Text>
        </View>
        <View style={{ ...styles.tableRow, backgroundColor: '#EFF6FF' }}>
          <Text style={styles.tableCellBold}>TOTAL REVENUE</Text>
          <Text style={{ ...styles.tableCellRight, fontWeight: 'bold' }}>$2,861,000</Text>
          <Text style={styles.tableCellRight}>100%</Text>
        </View>
      </View>

      <Text style={styles.subSectionTitle}>Expense Breakdown</Text>
      <View style={styles.table}>
        <View style={styles.tableHeader}>
          <Text style={styles.tableHeaderCell}>Category</Text>
          <Text style={styles.tableHeaderCellRight}>Amount</Text>
          <Text style={styles.tableHeaderCellRight}>% of Total</Text>
        </View>
        <View style={styles.tableRow}>
          <Text style={styles.tableCell}>Personnel (9 FTE + contractors)</Text>
          <Text style={styles.tableCellRight}>$1,129,000</Text>
          <Text style={styles.tableCellRight}>68%</Text>
        </View>
        <View style={{ ...styles.tableRow, ...styles.tableRowAlt }}>
          <Text style={styles.tableCell}>Infrastructure</Text>
          <Text style={styles.tableCellRight}>$140,000</Text>
          <Text style={styles.tableCellRight}>8%</Text>
        </View>
        <View style={styles.tableRow}>
          <Text style={styles.tableCell}>Marketing</Text>
          <Text style={styles.tableCellRight}>$160,000</Text>
          <Text style={styles.tableCellRight}>10%</Text>
        </View>
        <View style={{ ...styles.tableRow, ...styles.tableRowAlt }}>
          <Text style={styles.tableCell}>Operations</Text>
          <Text style={styles.tableCellRight}>$164,000</Text>
          <Text style={styles.tableCellRight}>10%</Text>
        </View>
        <View style={styles.tableRow}>
          <Text style={styles.tableCell}>Software & Tools</Text>
          <Text style={styles.tableCellRight}>$56,000</Text>
          <Text style={styles.tableCellRight}>3%</Text>
        </View>
        <View style={{ ...styles.tableRow, ...styles.tableRowAlt, backgroundColor: '#FEE2E2' }}>
          <Text style={styles.tableCellBold}>TOTAL EXPENSES</Text>
          <Text style={{ ...styles.tableCellRight, fontWeight: 'bold' }}>$1,649,000</Text>
          <Text style={styles.tableCellRight}>100%</Text>
        </View>
      </View>

      <View style={styles.noteBox}>
        <Text style={styles.noteText}>
          <Text style={{ fontWeight: 'bold' }}>Key Hires:</Text> VP Customer Success, Implementation Specialist, Support Lead, 2nd Senior Dev, DevOps Engineer. Founder starts modest $120K salary.
        </Text>
      </View>
      <PageFooter />
    </Page>

    {/* Year 3 Detail */}
    <Page size="A4" style={styles.page}>
      <PageHeader title="Year 3 - Scale Phase" />
      <View style={styles.yearHeader}>
        <Text style={styles.yearHeaderText}>Year 3 — Scale Phase</Text>
        <Text style={styles.yearPhase}>Team of 19 | 180% revenue growth | 48% profit margin</Text>
      </View>

      <View style={styles.metricsRow}>
        <View style={{ ...styles.metricCard, backgroundColor: '#EFF6FF' }}>
          <Text style={styles.metricValue}>$8.01M</Text>
          <Text style={styles.metricLabel}>Total Revenue</Text>
        </View>
        <View style={{ ...styles.metricCard, backgroundColor: '#FEE2E2' }}>
          <Text style={styles.metricValue}>$4.13M</Text>
          <Text style={styles.metricLabel}>Total Expenses</Text>
        </View>
        <View style={{ ...styles.metricCard, backgroundColor: '#ECFDF5' }}>
          <Text style={{ ...styles.metricValue, color: colors.success }}>$3.88M</Text>
          <Text style={styles.metricLabel}>Net Income</Text>
        </View>
        <View style={{ ...styles.metricCard, backgroundColor: '#F0FDF4' }}>
          <Text style={{ ...styles.metricValue, color: colors.success }}>48.5%</Text>
          <Text style={styles.metricLabel}>Profit Margin</Text>
        </View>
      </View>

      <Text style={styles.subSectionTitle}>Customer Metrics</Text>
      <View style={styles.table}>
        <View style={styles.tableHeader}>
          <Text style={styles.tableHeaderCell}>Metric</Text>
          <Text style={styles.tableHeaderCellRight}>Value</Text>
          <Text style={styles.tableHeaderCellRight}>vs Y2</Text>
        </View>
        <View style={styles.tableRow}>
          <Text style={styles.tableCell}>Health System Customers</Text>
          <Text style={styles.tableCellRight}>32</Text>
          <Text style={{ ...styles.tableCellRight, color: colors.success }}>+129%</Text>
        </View>
        <View style={{ ...styles.tableRow, ...styles.tableRowAlt }}>
          <Text style={styles.tableCell}>Total Locations</Text>
          <Text style={styles.tableCellRight}>520 (58 + 462)</Text>
          <Text style={{ ...styles.tableCellRight, color: colors.success }}>+189%</Text>
        </View>
        <View style={styles.tableRow}>
          <Text style={styles.tableCell}>Avg Revenue per Location/Month</Text>
          <Text style={styles.tableCellRight}>$1,000</Text>
          <Text style={styles.tableCellRight}>-9% (enterprise)</Text>
        </View>
        <View style={{ ...styles.tableRow, ...styles.tableRowAlt }}>
          <Text style={styles.tableCell}>Avg Setup Fee per Customer</Text>
          <Text style={styles.tableCellRight}>$55,000</Text>
          <Text style={{ ...styles.tableCellRight, color: colors.success }}>+83%</Text>
        </View>
      </View>

      <Text style={styles.subSectionTitle}>Revenue Breakdown</Text>
      <View style={styles.table}>
        <View style={styles.tableHeader}>
          <Text style={styles.tableHeaderCell}>Revenue Stream</Text>
          <Text style={styles.tableHeaderCellRight}>Amount</Text>
          <Text style={styles.tableHeaderCellRight}>% of Total</Text>
        </View>
        <View style={styles.tableRow}>
          <Text style={styles.tableCell}>Recurring (Platform Fees)</Text>
          <Text style={styles.tableCellRight}>$6,240,000</Text>
          <Text style={styles.tableCellRight}>78%</Text>
        </View>
        <View style={{ ...styles.tableRow, ...styles.tableRowAlt }}>
          <Text style={styles.tableCell}>Setup Fees (18 new customers)</Text>
          <Text style={styles.tableCellRight}>$990,000</Text>
          <Text style={styles.tableCellRight}>12%</Text>
        </View>
        <View style={styles.tableRow}>
          <Text style={styles.tableCell}>Professional Services</Text>
          <Text style={styles.tableCellRight}>$350,000</Text>
          <Text style={styles.tableCellRight}>4%</Text>
        </View>
        <View style={{ ...styles.tableRow, ...styles.tableRowAlt }}>
          <Text style={styles.tableCell}>Expansion Revenue</Text>
          <Text style={styles.tableCellRight}>$280,000</Text>
          <Text style={styles.tableCellRight}>3%</Text>
        </View>
        <View style={styles.tableRow}>
          <Text style={styles.tableCell}>Transaction Fees</Text>
          <Text style={styles.tableCellRight}>$150,000</Text>
          <Text style={styles.tableCellRight}>2%</Text>
        </View>
        <View style={{ ...styles.tableRow, ...styles.tableRowAlt, backgroundColor: '#EFF6FF' }}>
          <Text style={styles.tableCellBold}>TOTAL REVENUE</Text>
          <Text style={{ ...styles.tableCellRight, fontWeight: 'bold' }}>$8,010,000</Text>
          <Text style={styles.tableCellRight}>100%</Text>
        </View>
      </View>

      <Text style={styles.subSectionTitle}>Expense Breakdown</Text>
      <View style={styles.table}>
        <View style={styles.tableHeader}>
          <Text style={styles.tableHeaderCell}>Category</Text>
          <Text style={styles.tableHeaderCellRight}>Amount</Text>
          <Text style={styles.tableHeaderCellRight}>% of Total</Text>
        </View>
        <View style={styles.tableRow}>
          <Text style={styles.tableCell}>Personnel (19 FTE + contractors)</Text>
          <Text style={styles.tableCellRight}>$2,656,000</Text>
          <Text style={styles.tableCellRight}>68%</Text>
        </View>
        <View style={{ ...styles.tableRow, ...styles.tableRowAlt }}>
          <Text style={styles.tableCell}>Infrastructure</Text>
          <Text style={styles.tableCellRight}>$324,000</Text>
          <Text style={styles.tableCellRight}>8%</Text>
        </View>
        <View style={styles.tableRow}>
          <Text style={styles.tableCell}>Marketing</Text>
          <Text style={styles.tableCellRight}>$427,000</Text>
          <Text style={styles.tableCellRight}>11%</Text>
        </View>
        <View style={{ ...styles.tableRow, ...styles.tableRowAlt }}>
          <Text style={styles.tableCell}>Operations</Text>
          <Text style={styles.tableCellRight}>$364,000</Text>
          <Text style={styles.tableCellRight}>9%</Text>
        </View>
        <View style={styles.tableRow}>
          <Text style={styles.tableCell}>Software & Tools</Text>
          <Text style={styles.tableCellRight}>$156,000</Text>
          <Text style={styles.tableCellRight}>4%</Text>
        </View>
        <View style={{ ...styles.tableRow, ...styles.tableRowAlt, backgroundColor: '#FEE2E2' }}>
          <Text style={styles.tableCellBold}>TOTAL EXPENSES</Text>
          <Text style={{ ...styles.tableCellRight, fontWeight: 'bold' }}>$3,927,000</Text>
          <Text style={styles.tableCellRight}>100%</Text>
        </View>
      </View>
      <PageFooter />
    </Page>

    {/* Personnel Detail */}
    <Page size="A4" style={styles.page}>
      <PageHeader title="Personnel Planning" />
      <Text style={styles.sectionTitle}>Personnel Planning by Year</Text>

      <Text style={styles.subSectionTitle}>Year 1 Team (3 FTE)</Text>
      <View style={styles.table}>
        <View style={styles.tableHeader}>
          <Text style={styles.tableHeaderCell}>Role</Text>
          <Text style={styles.tableHeaderCellRight}>Salary</Text>
          <Text style={styles.tableHeaderCellRight}>Notes</Text>
        </View>
        <View style={styles.tableRow}>
          <Text style={styles.tableCell}>CEO/Founder</Text>
          <Text style={styles.tableCellRight}>$0</Text>
          <Text style={styles.tableCellRight}>Deferred</Text>
        </View>
        <View style={{ ...styles.tableRow, ...styles.tableRowAlt }}>
          <Text style={styles.tableCell}>Customer Success Manager</Text>
          <Text style={styles.tableCellRight}>$75,000</Text>
          <Text style={styles.tableCellRight}>Onboarding + Support</Text>
        </View>
        <View style={styles.tableRow}>
          <Text style={styles.tableCell}>Senior Full-Stack Developer</Text>
          <Text style={styles.tableCellRight}>$145,000</Text>
          <Text style={styles.tableCellRight}>Core Platform</Text>
        </View>
        <View style={{ ...styles.tableRow, ...styles.tableRowAlt }}>
          <Text style={styles.tableCell}>Contractors</Text>
          <Text style={styles.tableCellRight}>$55,000</Text>
          <Text style={styles.tableCellRight}>Design, Overflow Dev</Text>
        </View>
        <View style={styles.tableRow}>
          <Text style={styles.tableCell}>Benefits (~15%)</Text>
          <Text style={styles.tableCellRight}>$35,000</Text>
          <Text style={styles.tableCellRight}></Text>
        </View>
        <View style={{ ...styles.tableRow, backgroundColor: '#EFF6FF' }}>
          <Text style={styles.tableCellBold}>TOTAL PERSONNEL</Text>
          <Text style={{ ...styles.tableCellRight, fontWeight: 'bold' }}>$310,000</Text>
          <Text style={styles.tableCellRight}></Text>
        </View>
      </View>

      <Text style={styles.subSectionTitle}>Year 2 Team (9 FTE)</Text>
      <View style={styles.table}>
        <View style={styles.tableHeader}>
          <Text style={styles.tableHeaderCell}>Role</Text>
          <Text style={styles.tableHeaderCellRight}>Salary</Text>
        </View>
        <View style={styles.tableRow}>
          <Text style={styles.tableCell}>CEO/Founder</Text>
          <Text style={styles.tableCellRight}>$120,000</Text>
        </View>
        <View style={{ ...styles.tableRow, ...styles.tableRowAlt }}>
          <Text style={styles.tableCell}>VP Customer Success</Text>
          <Text style={styles.tableCellRight}>$130,000</Text>
        </View>
        <View style={styles.tableRow}>
          <Text style={styles.tableCell}>Customer Success Manager</Text>
          <Text style={styles.tableCellRight}>$72,000</Text>
        </View>
        <View style={{ ...styles.tableRow, ...styles.tableRowAlt }}>
          <Text style={styles.tableCell}>Implementation Specialist</Text>
          <Text style={styles.tableCellRight}>$70,000</Text>
        </View>
        <View style={styles.tableRow}>
          <Text style={styles.tableCell}>Support Lead</Text>
          <Text style={styles.tableCellRight}>$68,000</Text>
        </View>
        <View style={{ ...styles.tableRow, ...styles.tableRowAlt }}>
          <Text style={styles.tableCell}>Senior Developer x2</Text>
          <Text style={styles.tableCellRight}>$285,000</Text>
        </View>
        <View style={styles.tableRow}>
          <Text style={styles.tableCell}>DevOps Engineer</Text>
          <Text style={styles.tableCellRight}>$125,000</Text>
        </View>
        <View style={{ ...styles.tableRow, ...styles.tableRowAlt }}>
          <Text style={styles.tableCell}>Contractors + Benefits + Taxes</Text>
          <Text style={styles.tableCellRight}>$259,000</Text>
        </View>
        <View style={{ ...styles.tableRow, backgroundColor: '#EFF6FF' }}>
          <Text style={styles.tableCellBold}>TOTAL PERSONNEL</Text>
          <Text style={{ ...styles.tableCellRight, fontWeight: 'bold' }}>$1,129,000</Text>
        </View>
      </View>

      <Text style={styles.subSectionTitle}>Year 3 Team (19 FTE)</Text>
      <View style={styles.table}>
        <View style={styles.tableHeader}>
          <Text style={styles.tableHeaderCell}>Department</Text>
          <Text style={styles.tableHeaderCellRight}>Headcount</Text>
          <Text style={styles.tableHeaderCellRight}>Total Cost</Text>
        </View>
        <View style={styles.tableRow}>
          <Text style={styles.tableCell}>Executive (CEO)</Text>
          <Text style={styles.tableCellRight}>1</Text>
          <Text style={styles.tableCellRight}>$180,000</Text>
        </View>
        <View style={{ ...styles.tableRow, ...styles.tableRowAlt }}>
          <Text style={styles.tableCell}>Sales (VP + 2 AEs)</Text>
          <Text style={styles.tableCellRight}>3</Text>
          <Text style={styles.tableCellRight}>$340,000</Text>
        </View>
        <View style={styles.tableRow}>
          <Text style={styles.tableCell}>Customer Success (VP + 3 CSMs + 2 Impl)</Text>
          <Text style={styles.tableCellRight}>6</Text>
          <Text style={styles.tableCellRight}>$506,000</Text>
        </View>
        <View style={{ ...styles.tableRow, ...styles.tableRowAlt }}>
          <Text style={styles.tableCell}>Support (Lead + 2 Reps)</Text>
          <Text style={styles.tableCellRight}>3</Text>
          <Text style={styles.tableCellRight}>$175,000</Text>
        </View>
        <View style={styles.tableRow}>
          <Text style={styles.tableCell}>Engineering (Mgr + 4 Devs + DevOps)</Text>
          <Text style={styles.tableCellRight}>6</Text>
          <Text style={styles.tableCellRight}>$880,000</Text>
        </View>
        <View style={{ ...styles.tableRow, ...styles.tableRowAlt }}>
          <Text style={styles.tableCell}>Contractors + Benefits + Taxes</Text>
          <Text style={styles.tableCellRight}>-</Text>
          <Text style={styles.tableCellRight}>$575,000</Text>
        </View>
        <View style={{ ...styles.tableRow, backgroundColor: '#EFF6FF' }}>
          <Text style={styles.tableCellBold}>TOTAL PERSONNEL</Text>
          <Text style={{ ...styles.tableCellRight, fontWeight: 'bold' }}>19</Text>
          <Text style={{ ...styles.tableCellRight, fontWeight: 'bold' }}>$2,656,000</Text>
        </View>
      </View>
      <PageFooter />
    </Page>

    {/* Key Assumptions */}
    <Page size="A4" style={styles.page}>
      <PageHeader title="Key Assumptions" />
      <Text style={styles.sectionTitle}>Key Assumptions & Notes</Text>

      <Text style={styles.subSectionTitle}>Revenue Assumptions</Text>
      <View style={styles.table}>
        <View style={styles.tableHeader}>
          <Text style={styles.tableHeaderCell}>Assumption</Text>
          <Text style={styles.tableHeaderCellRight}>Year 1</Text>
          <Text style={styles.tableHeaderCellRight}>Year 2</Text>
          <Text style={styles.tableHeaderCellRight}>Year 3</Text>
        </View>
        <View style={styles.tableRow}>
          <Text style={styles.tableCell}>Avg Revenue per Location/Month</Text>
          <Text style={styles.tableCellRight}>$1,200</Text>
          <Text style={styles.tableCellRight}>$1,100</Text>
          <Text style={styles.tableCellRight}>$1,000</Text>
        </View>
        <View style={{ ...styles.tableRow, ...styles.tableRowAlt }}>
          <Text style={styles.tableCell}>Avg Setup Fee per Customer</Text>
          <Text style={styles.tableCellRight}>$18,000</Text>
          <Text style={styles.tableCellRight}>$30,000</Text>
          <Text style={styles.tableCellRight}>$55,000</Text>
        </View>
        <View style={styles.tableRow}>
          <Text style={styles.tableCell}>Customer Churn (Annual)</Text>
          <Text style={styles.tableCellRight}>5%</Text>
          <Text style={styles.tableCellRight}>5%</Text>
          <Text style={styles.tableCellRight}>5%</Text>
        </View>
        <View style={{ ...styles.tableRow, ...styles.tableRowAlt }}>
          <Text style={styles.tableCell}>Expansion Revenue</Text>
          <Text style={styles.tableCellRight}>-</Text>
          <Text style={styles.tableCellRight}>10%</Text>
          <Text style={styles.tableCellRight}>10%</Text>
        </View>
      </View>

      <Text style={styles.subSectionTitle}>Model Philosophy: Bootstrap-First</Text>
      <View style={styles.summaryBox}>
        <Text style={styles.summaryText}>
          This financial model is designed to be profitable from Year 1 without external funding. The lean approach:
        </Text>
      </View>
      
      <View style={{ marginTop: 10 }}>
        {[
          'Founder salary deferred in Year 1 to maximize runway',
          'Player/coach managers who do work, not just manage',
          'Strategic use of contractors for overflow, not permanent staff',
          'Marketing focused on high-ROI channels (trade shows, referrals)',
          'Infrastructure scales with revenue, not ahead of it',
          'Each hire justified by specific revenue milestone',
        ].map((item, idx) => (
          <View key={idx} style={{ flexDirection: 'row', marginBottom: 5 }}>
            <Text style={{ fontSize: 10, color: colors.success, marginRight: 8 }}>✓</Text>
            <Text style={{ fontSize: 10, color: colors.dark }}>{item}</Text>
          </View>
        ))}
      </View>

      <Text style={styles.subSectionTitle}>What This Model Achieves</Text>
      <View style={styles.metricsRow}>
        <View style={{ ...styles.metricCard, backgroundColor: '#ECFDF5' }}>
          <Text style={{ ...styles.metricValue, fontSize: 14, color: colors.success }}>$5.56M</Text>
          <Text style={styles.metricLabel}>3-Year Cumulative Profit</Text>
        </View>
        <View style={{ ...styles.metricCard, backgroundColor: '#EFF6FF' }}>
          <Text style={{ ...styles.metricValue, fontSize: 14 }}>51%</Text>
          <Text style={styles.metricLabel}>Year 3 Profit Margin</Text>
        </View>
        <View style={{ ...styles.metricCard, backgroundColor: '#FEF3C7' }}>
          <Text style={{ ...styles.metricValue, fontSize: 14 }}>520</Text>
          <Text style={styles.metricLabel}>Locations by Y3</Text>
        </View>
        <View style={{ ...styles.metricCard, backgroundColor: '#F5F3FF' }}>
          <Text style={{ ...styles.metricValue, fontSize: 14 }}>19</Text>
          <Text style={styles.metricLabel}>Team Size Y3</Text>
        </View>
      </View>

      <View style={{ ...styles.noteBox, marginTop: 15 }}>
        <Text style={styles.noteText}>
          <Text style={{ fontWeight: 'bold' }}>Investment Rationale:</Text> The $900K seed round provides 12-month operating runway with CEO at fair market salary, plus growth acceleration capital. This enables earlier sales hires, faster platform development, and aggressive marketing — potentially reaching Year 3 revenue targets a year earlier while de-risking the business.
        </Text>
      </View>

      <View style={{ marginTop: 20, padding: 15, backgroundColor: colors.primary, borderRadius: 8 }}>
        <Text style={{ fontSize: 12, color: '#FFFFFF', textAlign: 'center' }}>
          Questions? Contact George Broughton III
        </Text>
        <Text style={{ fontSize: 10, color: colors.accent, textAlign: 'center', marginTop: 5 }}>
          george.broughton@sterilemate.com
        </Text>
      </View>
      <PageFooter />
    </Page>
  </Document>
  );
};

export default FinancialProjectionsPDF;
